import * as THREE from 'three';
import * as CANNON from 'cannon-es';

export class BoostPad {
  constructor(scene, physicsWorld, position, isLarge = false) {
    this.scene = scene;
    this.physicsWorld = physicsWorld;
    this.position = position;
    this.isLarge = isLarge;
    
    // Boost pad properties based on Rocket League specs
    this.radius = isLarge ? 208/100 : 144/100; // Convert from uu to our scale
    this.height = 0.5;
    this.respawnTime = isLarge ? 10000 : 4000; // 10 seconds for big, 4 for small
    this.boostAmount = isLarge ? 100 : 12; // 100% for big, 12% for small
    this.isActive = true;
    this.lastCollectedTime = 0;
    this.respawnTimer = null;
    
    // Create mesh and hitbox
    this.createBoostPadMesh();
    this.createBoostPadHitbox();
  }
  
  createBoostPadMesh() {
    // Create base geometry
    const baseGeometry = new THREE.CylinderGeometry(this.radius, this.radius, this.height, 16);
    const baseMaterial = new THREE.MeshPhongMaterial({
      color: this.isLarge ? 0xffcc00 : 0x00ccff,
      emissive: this.isLarge ? 0x664400 : 0x003366,
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.8,
      shininess: 80
    });
    
    this.mesh = new THREE.Mesh(baseGeometry, baseMaterial);
    this.mesh.position.set(this.position.x, this.position.y, this.position.z);
    this.mesh.rotation.x = Math.PI / 2; // Lay flat
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = true;
    
    // Add glow effect
    const glowGeometry = new THREE.CylinderGeometry(
      this.radius * 1.1, 
      this.radius * 1.1, 
      this.height * 0.2, 
      16
    );
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: this.isLarge ? 0xffcc00 : 0x00ccff,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide
    });
    
    this.glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
    this.glowMesh.position.y = 0.1;
    this.mesh.add(this.glowMesh);
    
    // Create a rotating inner part
    const innerGeometry = new THREE.BoxGeometry(
      this.radius * 0.7, 
      this.height * 0.3, 
      this.radius * 0.7
    );
    const innerMaterial = new THREE.MeshPhongMaterial({
      color: this.isLarge ? 0xffee66 : 0x66ddff,
      emissive: this.isLarge ? 0x664400 : 0x003366,
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.9
    });
    
    this.innerMesh = new THREE.Mesh(innerGeometry, innerMaterial);
    this.innerMesh.position.y = 0.25;
    this.mesh.add(this.innerMesh);
    
    // Add to scene
    this.scene.add(this.mesh);
    
    // Create a particles system for collection effect
    this.particles = [];
    const particleGeometry = new THREE.SphereGeometry(0.1, 8, 8);
    const particleMaterial = new THREE.MeshBasicMaterial({
      color: this.isLarge ? 0xffcc00 : 0x00ccff,
      transparent: true,
      opacity: 0.8
    });
    
    for (let i = 0; i < 8; i++) {
      const particle = new THREE.Mesh(particleGeometry, particleMaterial);
      particle.visible = false;
      this.scene.add(particle);
      this.particles.push({
        mesh: particle,
        velocity: new THREE.Vector3(),
        active: false
      });
    }
  }
  
  createBoostPadHitbox() {
    // Create a cylindrical hitbox (using a kinematic body with no collision responses)
    this.hitbox = new CANNON.Body({
      mass: 0, // Static body
      position: new CANNON.Vec3(this.position.x, this.position.y, this.position.z),
      shape: new CANNON.Cylinder(this.radius, this.radius, this.height, 8),
      collisionResponse: false, // Don't respond to collisions physically
      type: CANNON.Body.KINEMATIC
    });
    
    // Custom property to identify as boost pad
    this.hitbox.isBoostPad = true;
    this.hitbox.boostPadReference = this;
    
    // Add to physics world
    this.physicsWorld.addBody(this.hitbox);
  }
  
  collect() {
    if (!this.isActive) return 0;
    
    // Deactivate the pad
    this.isActive = false;
    this.lastCollectedTime = Date.now();
    
    // Hide the meshes
    this.mesh.visible = false;
    
    // Trigger particle effect
    this.triggerCollectionEffect();
    
    // Set respawn timer
    this.respawnTimer = setTimeout(() => this.respawn(), this.respawnTime);
    
    // Return boost amount
    return this.boostAmount;
  }
  
  respawn() {
    // Reactivate the pad
    this.isActive = true;
    this.mesh.visible = true;
    this.respawnTimer = null;
  }
  
  triggerCollectionEffect() {
    // Activate particles
    for (let i = 0; i < this.particles.length; i++) {
      const particle = this.particles[i];
      
      // Position at pad location
      particle.mesh.position.copy(this.mesh.position);
      
      // Random velocity in upward hemisphere
      const angle = Math.random() * Math.PI * 2;
      const speed = 0.05 + Math.random() * 0.1;
      particle.velocity.set(
        Math.cos(angle) * speed,
        0.1 + Math.random() * 0.1,
        Math.sin(angle) * speed
      );
      
      particle.active = true;
      particle.mesh.visible = true;
      particle.life = 30; // Frames of life
    }
  }
  
  update() {
    // Rotate inner part for visual effect
    if (this.innerMesh && this.isActive) {
      this.innerMesh.rotation.y += 0.02;
      this.glowMesh.rotation.y -= 0.01;
    }
    
    // Update particles
    for (let i = 0; i < this.particles.length; i++) {
      const particle = this.particles[i];
      if (particle.active) {
        // Move particle
        particle.mesh.position.add(particle.velocity);
        
        // Apply gravity
        particle.velocity.y -= 0.005;
        
        // Update life
        particle.life--;
        
        // Fade out
        if (particle.life < 10) {
          particle.mesh.material.opacity = particle.life / 10;
        }
        
        // Deactivate if life is over
        if (particle.life <= 0) {
          particle.active = false;
          particle.mesh.visible = false;
        }
      }
    }
  }
  
  // Check if a car can collect this boost pad
  checkCollection(car) {
    if (!this.isActive) return false;
    
    // Get car position
    const carPos = car.getPosition();
    
    // Calculate distance to car (cylindrical distance - XZ plane only)
    const dx = this.position.x - carPos.x;
    const dz = this.position.z - carPos.z;
    const distance = Math.sqrt(dx * dx + dz * dz);
    
    // Vertical distance check (car must be close to ground)
    const dy = Math.abs(this.position.y - carPos.y);
    
    // If car is within radius and height range, collect the boost
    if (distance < this.radius && dy < 1.5) {
      const boostAmount = this.collect();
      return boostAmount;
    }
    
    return 0;
  }
  
  dispose() {
    // Clean up meshes and physics
    if (this.mesh) {
      this.scene.remove(this.mesh);
      this.mesh.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(material => material.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
    }
    
    // Clean up particles
    this.particles.forEach(particle => {
      if (particle.mesh) {
        this.scene.remove(particle.mesh);
        if (particle.mesh.geometry) particle.mesh.geometry.dispose();
        if (particle.mesh.material) particle.mesh.material.dispose();
      }
    });
    
    // Remove hitbox from physics world
    if (this.hitbox && this.physicsWorld) {
      this.physicsWorld.removeBody(this.hitbox);
    }
    
    // Clear timeout if active
    if (this.respawnTimer) {
      clearTimeout(this.respawnTimer);
      this.respawnTimer = null;
    }
  }
} 